import { ComponentType, ReactNode, createContext, useContext, useMemo, useRef } from 'react';
import { CallObject } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { LocationFeatureCreditDetails } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { ReactFlowProvider, Node as ReactFlowNode, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CallRouteV1 } from '@frontend/api-call-route';
import { FlowContent, BasicNode, CustomEdgeData, EdgeType } from '@frontend/node-flow';
import { IncomingDialable } from '../../helpers/incoming-dialables';
import { logError } from '../../helpers/utils';
import { CustomNodeProps, DataPayload, extendableNodes, SharedNodeProps } from './data';
import { BreakHourNode } from './nodes/break-hour-node';
import { FallbackNode } from './nodes/fallback-node';
import { HoursTypeNode } from './nodes/hours-type-node';
import {
  IncomingDialablesNode,
  INCOMING_DIALABLES_SOURCE_HANDLE_ID,
  INCOMING_DIALABLES_TARGET_HANDLE_ID,
} from './nodes/incoming-dialables-node';
import {
  VoicemailNode,
  CallGroupNode,
  PlayNode,
  ForwardingDeviceNode,
  ForwardingNumberNode,
  PhoneTreeNode,
  OfficeHoursNode,
  CallQueueNode,
  RepeatNode,
  CallRouteNode,
  WeaveAssistantNode,
} from './nodes/instruction-nodes';
import { StartNode } from './nodes/start-node';
import { TreeOptionNode } from './nodes/tree-option-node';

const INCOMING_DIALABLES_NODE_TYPE = 'incomingDialables';
const INCOMING_DIALABLES_NODE_ID = 'incoming-dialables-node';
const INCOMING_DIALABLES_EDGE_TYPE = EdgeType.ToTargetShort;
type FlowNodeType = CallRouteV1.Types.NodeType | typeof INCOMING_DIALABLES_NODE_TYPE;

type FlowNodeReactProps = NodeProps<ReactFlowNode<DataPayload, FlowNodeType>>;
type CallRouteNodeReactProps = NodeProps<ReactFlowNode<DataPayload, CallRouteV1.Types.NodeType>>;
type FlowNodeComponent<TNodeType extends string> = ComponentType<SharedNodeProps<TNodeType>>;

const CallRouteFlowNodePropsContext = createContext<CustomNodeProps | null>(null);
const EMPTY_NODE_PROPS: CustomNodeProps = {};

const useCallRouteFlowNodeProps = () => {
  const value = useContext(CallRouteFlowNodePropsContext);
  const hasLoggedMissingContextRef = useRef(false);

  if (!value) {
    if (!hasLoggedMissingContextRef.current) {
      // Unique ID for searching logs for this specific error: PC-6qj7w41
      logError('CallRouteFlowNodePropsContext is missing: PC-6qj7w41', 'useCallRouteFlowNodeProps', {});
      hasLoggedMissingContextRef.current = true;
    }

    return EMPTY_NODE_PROPS;
  }

  return value;
};

const asCallRouteNodeProps = (props: FlowNodeReactProps): CallRouteNodeReactProps => props as CallRouteNodeReactProps;
const asFlowNodeProps = (props: FlowNodeReactProps): FlowNodeReactProps => props;

const withCommonProps = <TNodeType extends string>(
  Component: FlowNodeComponent<TNodeType>,
  mapProps: (props: FlowNodeReactProps) => NodeProps<ReactFlowNode<DataPayload, TNodeType>>
) => {
  const Wrapped = (props: FlowNodeReactProps) => {
    const commonProps = useCallRouteFlowNodeProps();
    return <Component {...mapProps(props)} {...commonProps} />;
  };
  Wrapped.displayName = `withCommonProps(${Component.displayName ?? Component.name ?? 'Node'})`;
  return Wrapped;
};

// Keep nodeTypes as a module-level constant so React Flow receives a stable object identity.
const flowNodeTypes: Record<string, (props: FlowNodeReactProps) => ReactNode> = {
  [CallRouteV1.Types.NodeType.NODE_TYPE_START]: withCommonProps(StartNode, asCallRouteNodeProps),
  [INCOMING_DIALABLES_NODE_TYPE]: withCommonProps(IncomingDialablesNode, asFlowNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP]: withCommonProps(CallGroupNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE]: withCommonProps(CallRouteNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION]: withCommonProps(CallRouteNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS]: withCommonProps(OfficeHoursNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS]: withCommonProps(HoursTypeNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS]: withCommonProps(HoursTypeNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS]: withCommonProps(BreakHourNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE]: withCommonProps(PlayNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE]: withCommonProps(PhoneTreeNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION]: withCommonProps(TreeOptionNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE]: withCommonProps(ForwardingDeviceNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER]: withCommonProps(ForwardingNumberNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX]: withCommonProps(VoicemailNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE]: withCommonProps(CallQueueNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT]: withCommonProps(RepeatNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT]: withCommonProps(WeaveAssistantNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK]: withCommonProps(FallbackNode, asCallRouteNodeProps),
  [CallRouteV1.Types.NodeType.NODE_TYPE_UNSPECIFIED]: () => null,
  [CallRouteV1.Types.NodeType.NODE_TYPE_BOOLEAN]: (props) => <BasicNode {...props} type='basic' />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_TERMINATE]: (props) => <BasicNode {...props} type='basic' />,
};

type ExtendedNode = CallRouteV1.Types.Node &
  Pick<DataPayload, 'isExtendable' | 'isPhoneTreeDescendent' | 'warning' | 'placeholderWarning'>;

export function CallRouteFlow({
  nodes,
  edges,
  canEdit = false,
  hoursManagementPermission,
  showDebugButtons = false,
  isDeviceExtensionRoute = false,
  incomingDialables = [],
  locationCreditDetails,
  callRoutePhoneNumberLocationIds,
  onCallObjectLinkClick,
  onEditIconClick,
  onPlusIconClick,
  onDeleteIconClick,
  onIncomingDialablesMoreClick,
  AudioPlayerComponent,
  DayHoursListComponent,
  initialNodeId,
}: {
  nodes: ExtendedNode[];
  edges: { sourceId: string; targetId: string; label?: string; type?: string }[];
  canEdit?: boolean;
  hoursManagementPermission?: boolean;
  showDebugButtons?: boolean;
  initialNodeId?: string;
  isDeviceExtensionRoute?: boolean;
  incomingDialables?: IncomingDialable[];
  locationCreditDetails?: Record<string, LocationFeatureCreditDetails>;
  callRoutePhoneNumberLocationIds?: string[];
  onPlusIconClick?: (data: { parentNodeId: string }) => void;
  onCallObjectLinkClick?: SharedNodeProps['onCallObjectLinkClick'];
  onEditIconClick?: SharedNodeProps['onEditIconClick'];
  onDeleteIconClick?: SharedNodeProps['onDeleteIconClick'];
  onIncomingDialablesMoreClick?: () => void;
  AudioPlayerComponent: SharedNodeProps['AudioPlayerComponent'];
  DayHoursListComponent: SharedNodeProps['DayHoursListComponent'];
}) {
  const commonProps: CustomNodeProps = useMemo(
    () => ({
      canEdit,
      hoursManagementPermission,
      isDeviceExtensionRoute,
      incomingDialables,
      locationCreditDetails,
      callRoutePhoneNumberLocationIds,
      onCallObjectLinkClick,
      onEditIconClick,
      onDeleteIconClick,
      onIncomingDialablesMoreClick,
      AudioPlayerComponent,
      DayHoursListComponent,
    }),
    [
      AudioPlayerComponent,
      DayHoursListComponent,
      isDeviceExtensionRoute,
      incomingDialables,
      locationCreditDetails,
      callRoutePhoneNumberLocationIds,
      onDeleteIconClick,
      onIncomingDialablesMoreClick,
      canEdit,
      hoursManagementPermission,
      onCallObjectLinkClick,
      onEditIconClick,
    ]
  );
  const memoizedNodes: ReactFlowNode<DataPayload, FlowNodeType | 'basic'>[] = useMemo(() => {
    const mappedNodes: ReactFlowNode<DataPayload, FlowNodeType | 'basic'>[] = nodes.map((node) => {
      let parentNodeType: CallRouteV1.Types.NodeType | undefined;
      if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK) {
        const parentEdge = edges.find((edge) => edge.targetId === node.id);
        if (parentEdge) {
          const parentNode = nodes.find((n) => n.id === parentEdge.sourceId);
          if (parentNode) {
            parentNodeType = parentNode.type;
          }
        }
      }

      return {
        id: node.id,
        type: Object.values(CallRouteV1.Types.NodeType).includes(node.type) ? node.type : 'basic',
        // Need to set a default position for the node since it is required by React Flow
        position: { x: 0, y: 0 },
        data: {
          id: node.id,
          label: node.callObject.primitiveName,
          warning: node.warning || '',
          callObject: node.callObject as CallObject,
          isPhoneTreeDescendent: node.isPhoneTreeDescendent,
          isExtendable: node.isExtendable,
          parentNodeType,
          placeholderWarning: node.placeholderWarning,
        },
      };
    });

    const hasIncomingDialables = incomingDialables.length > 0;
    const startNode = nodes.find((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_START);
    if (!hasIncomingDialables || !startNode) {
      return mappedNodes;
    }

    // Guard against re-adding the synthetic node on recompute/re-render.
    const alreadyHasIncomingDialablesNode = mappedNodes.some((node) => node.id === INCOMING_DIALABLES_NODE_ID);
    if (alreadyHasIncomingDialablesNode) {
      return mappedNodes;
    }

    mappedNodes.push({
      id: INCOMING_DIALABLES_NODE_ID,
      type: INCOMING_DIALABLES_NODE_TYPE,
      position: { x: 0, y: 0 },
      data: {
        id: INCOMING_DIALABLES_NODE_ID,
        label: 'Phone Numbers',
        warning: '',
        isExtendable: false,
        isPhoneTreeDescendent: false,
      },
    });

    return mappedNodes;
  }, [nodes, edges, incomingDialables]);

  const memoizedEdges = useMemo(() => {
    const mappedEdges: Array<{
      id: string;
      source: string;
      target: string;
      type: string;
      selectable: boolean;
      sourceHandle?: string;
      targetHandle?: string;
      data: CustomEdgeData;
    }> = edges.map((edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.sourceId);

      return {
        id: edge.sourceId + ':' + edge.targetId,
        source: edge.sourceId,
        target: edge.targetId,
        type: edge.type ?? 'basic',
        selectable: false, // make the edges unselectable so that you can't drag them
        data: {
          isExtendable: sourceNode?.type
            ? extendableNodes.includes(sourceNode.type as CallRouteV1.Types.NodeType)
            : false,
          label: edge.label,
        },
      };
    });

    const hasIncomingDialables = incomingDialables.length > 0;
    const startNode = nodes.find((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_START);
    if (!hasIncomingDialables || !startNode) {
      return mappedEdges;
    }

    const incomingDialablesEdgeId = `${startNode.id}:${INCOMING_DIALABLES_NODE_ID}`;
    const alreadyHasIncomingDialablesEdge = mappedEdges.some((edge) => edge.id === incomingDialablesEdgeId);
    if (alreadyHasIncomingDialablesEdge) {
      return mappedEdges;
    }

    mappedEdges.push({
      id: incomingDialablesEdgeId,
      source: startNode.id,
      target: INCOMING_DIALABLES_NODE_ID,
      type: INCOMING_DIALABLES_EDGE_TYPE,
      selectable: false,
      sourceHandle: INCOMING_DIALABLES_SOURCE_HANDLE_ID,
      targetHandle: INCOMING_DIALABLES_TARGET_HANDLE_ID,
      data: {
        isExtendable: false,
        label: undefined,
        useManualLayout: true,
      },
    });

    return mappedEdges;
  }, [edges, nodes, incomingDialables]);

  // The default array of nodes to show when no nodes are provided, it will show a start node.
  const defaultEmptyNodes: ReactFlowNode<DataPayload, FlowNodeType>[] = useMemo(
    () => [
      {
        id: 'start',
        type: CallRouteV1.Types.NodeType.NODE_TYPE_START,
        // Need to set a default position for the node since it is required by React Flow
        position: { x: 0, y: 0 },
        data: {
          id: 'start',
          label: 'Incoming Call',
          warning: '',
          isExtendable: true,
          isPhoneTreeDescendent: false,
          callObject: {
            primitiveName: 'Incoming Call',
            primitiveId: '',
            instructionId: '',
            instructionSetId: '',
          } as CallObject,
        },
      },
    ],
    []
  );

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <CallRouteFlowNodePropsContext.Provider value={commonProps}>
          <FlowContent<FlowNodeType, DataPayload>
            nodes={
              !!nodes.length ? memoizedNodes : defaultEmptyNodes // Use default empty nodes if no nodes are provided
            }
            edges={memoizedEdges}
            canEdit={canEdit}
            customNodeTypes={flowNodeTypes}
            onPlusIconClick={onPlusIconClick}
            showDebugButtons={showDebugButtons}
            initialNodeId={initialNodeId}
          />
        </CallRouteFlowNodePropsContext.Provider>
      </ReactFlowProvider>
    </div>
  );
}
